// client/src/hooks/useChatbot.js
// Manages Prof-iX chatbot state and API calls.
// Matches pattern of useCognitive.js — GlobalContext + useCallback.
//
// Usage:
//   const { messages, isLoading, sendChatMessage, clearChat, reindexContent, indexStatus } = useChatbot();

import { useContext, useCallback, useState } from 'react';
import { GlobalContext } from '../context/provider';
import chatbotAPI from '../api/chatbotAPI';

const useChatbot = () => {
  const { authState: { authData } } = useContext(GlobalContext);

  // ── Local state (not in GlobalContext — chatbot state is per-session, per-component) ──
  const [messages,    setMessages]    = useState([]);   // { id, role, content, sources, timestamp, error }
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState(null);

  // ============================================================================
  // SEND MESSAGE
  // ============================================================================

  /**
   * Send a user message and append Prof-iX's response to messages[].
   * @param {string} courseId
   * @param {string} courseName
   * @param {string} messageText
   */
  const sendChatMessage = useCallback(async (courseId, courseName, category, messageText) => {
    if (!messageText?.trim() || isLoading) return null;

    const trimmed = messageText.trim();

    // Add user message immediately (optimistic UI)
    const userMsg = {
      id:        Date.now(),
      role:      'user',
      content:   trimmed,
      timestamp: new Date(),
    };

    setMessages(prev => {
      const updated = [...prev, userMsg];

      // Build history from existing messages (exclude greeting id=1 and error messages)
      const history = updated
        .filter(m => !m.error && m.id !== 1)
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      // Fire API call (inside setter to capture latest messages)
      _callAPI(courseId, courseName, category, trimmed, history);
      

      return updated;
    });

    return userMsg;
  }, [isLoading]);

  // Internal API call — separated so sendMessage stays clean
  const _callAPI = async (courseId, courseName, category, message, history) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatbotAPI.sendChatMessage(courseId, courseName, category, message, history);

      if (!response.success) {
        throw new Error(response.error || 'Prof-iX did not respond.');
      }

      const assistantMsg = {
        id:        Date.now() + 1,
        role:      'assistant',
        content:   response.answer,
        sources:   response.sources || [],
        model:     response.model,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMsg]);
      return assistantMsg;

    } catch (err) {
      console.error('❌ useChatbot.sendChatMessage error:', err);
      const errorMsg = {
        id:        Date.now() + 1,
        role:      'assistant',
        content:   'Sorry, I had trouble connecting. Please try again in a moment.',
        error:     true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      setError(err.message);
      return null;

    } finally {
      setIsLoading(false);
    }
  };

  const sendGatewayMessage = useCallback(async (messageText) => {
    if (!messageText?.trim() || isLoading) return null;

    const trimmed = messageText.trim();
    const userMsg = {
      id:        Date.now(),
      role:      'user',
      content:   trimmed,
      timestamp: new Date(),
    };

    setMessages(prev => {
      const updated = [...prev, userMsg];
      const history = updated
        .filter(m => !m.error && m.id !== 1)
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));
      _callGatewayAPI(trimmed, history);
      return updated;
    });

    return userMsg;
  }, [isLoading]);

  const _callGatewayAPI = async (message, history) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatbotAPI.sendGatewayMessage(message, history);
      if (!response.success) throw new Error(response.error || 'Prof. iX did not respond.');

      const assistantMsg = {
        id:        Date.now() + 1,
        role:      'assistant',
        content:   response.answer,
        sources:   response.sources || [],
        model:     response.model,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id:        Date.now() + 1,
        role:      'assistant',
        content:   'Sorry, I had trouble connecting. Please try again.',
        error:     true,
        timestamp: new Date(),
      }]);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // CLEAR CHAT
  // ============================================================================

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  // ============================================================================
  // CLEAR ERROR
  // ============================================================================

  const clearError = useCallback(() => setError(null), []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    messages,
    isLoading,
    error,

    // Chat
    sendChatMessage,
    sendGatewayMessage,
    clearChat,

    // Utility
    clearError,
  };
};

export default useChatbot;